import React, { FC, useMemo } from 'react'

/* mui */
import { Stack, Box, Button, useTheme } from '@mui/material'

/* icons */
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import DashboardIcon from '@mui/icons-material/Dashboard'

/* helpers */
import { getHasHighCheckPriority } from '../../../helpers/getHasHighCheckPriority'

/* component */
import { Copyright } from '../../../components/molecules/Copyright'
import { MessageOverlay } from '../../../components/molecules/MessageOverlay'
import { OCR_ERROR_MESSAGE_LEGACY } from '../../../constants/const'
import { MergedBasicResultWindow } from '../../../components/organisms/BasicResult'
import { RPBasedMedicineResult } from '../../../components/organisms/MedicineResult'
import { QrImagesModalBody } from '../../../components/templates/QrImagesModalBody'
import { InitialScreenBody } from '../../../components/templates/InitialScreenBody'
import { CustomFab } from '../../../components/molecules/CustomFab'
import { RequestIdDisplay } from '../../../components/organisms/RequestIdDisplay'

/* hooks */
import {
  usePrescriptions,
  useSelectedPresc,
  usePrescriptionImageBox,
  useEditPresc,
  useCameraModal as useCamera,
} from '../../../features/prescription'
import {
  useModal,
  useMedicationAlert,
  medicationAlertOpenButton,
  DrawerToggleButton,
} from '../../../shared'
import { useReportPresc } from '../hooks/useReport'
import { useNavigate } from 'react-router-dom'

const TMNIndexPage: FC = () => {
  const { AdditionalPrescSnackBar, prescriptions, LoadingOverlay } =
    usePrescriptions()
  const { selectedPresc: userPresc, selectOcrRequest } = useSelectedPresc({
    prescriptions,
  })

  const theme = useTheme()
  const selectedPresc = userPresc

  const { editBasicResult, updatingPrescription } = useEditPresc({
    prescription: selectedPresc,
  })

  const {
    reportBasicResult,
    reportRPIndex,
    openModal,
    renderSnackBar,
    ReportSendingOverlay,
  } = useReportPresc({
    prescription: selectedPresc,
  })

  const { renderCameraModal, OpenButton, DuringOcrOverlay } = useCamera()

  const { MedicationModal, setMedicationAlertOpen } = useMedicationAlert(
    selectedPresc?.id,
    selectedPresc?.low_accuracy_alerts,
    selectedPresc?.reshape_reports,
    selectedPresc?.gigi_alerts,
    selectedPresc?.med_change_alerts
  )

  const navigate = useNavigate()

  const PortalNavigateButton = (
    <>
      <Button
        sx={{
          width: '100%',
          whiteSpace: 'pre-wrap',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
        onClick={() => navigate('/portal')}
      >
        <DashboardIcon sx={{ fontSize: 20 }} />
        {'管理ポータルへ'}
      </Button>
    </>
  )

  const drawerButtomButtons = [PortalNavigateButton]

  const prescImages = useMemo(
    () => selectedPresc?.presc_images || [],
    [selectedPresc]
  )

  const prescImagesArea = useMemo(
    () => selectedPresc?.basic_result?.処方箋存在領域?.selected_option || [],
    [selectedPresc]
  )

  const { prescImageShown, PrescImageBox, PrescImageToggleButton } =
    usePrescriptionImageBox(prescImages, prescImagesArea)

  const { openModal: openQrImageModal, renderModal: renderQrImageModal } =
    useModal(
      selectedPresc && <QrImagesModalBody prescription={selectedPresc} />,
      'center'
    )

  if (!selectedPresc) {
    return (
      <>
        {LoadingOverlay}
        <DrawerToggleButton
          PrescImageToggleButton={PrescImageToggleButton}
          prescriptions={prescriptions}
          selectOcrRequest={selectOcrRequest}
          BottomButtons={drawerButtomButtons}
        />
        <InitialScreenBody />
        {DuringOcrOverlay}
        {OpenButton}
        {renderCameraModal()}
      </>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.palette.secondary.light,
      }}
    >
      {DuringOcrOverlay}
      {ReportSendingOverlay}
      <DrawerToggleButton
        selectedPresc={selectedPresc}
        PrescImageToggleButton={PrescImageToggleButton}
        prescriptions={prescriptions}
        selectOcrRequest={selectOcrRequest}
        BottomButtons={drawerButtomButtons}
      />
      {PrescImageBox}
      <Box
        sx={{
          [theme.breakpoints.down('md')]: prescImageShown
            ? {
                display: 'none',
              }
            : {
                width: '100%',
              },
        }}
        width="50%"
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'scroll',
            height: '100vh',
          }}
        >
          {selectedPresc.was_read_using_jahis_qr ? (
            // JAHIS QR読み込み時はオーバーレイのみのシンプルUI（FABボタンは表示）
            <>
              {MessageOverlay(
                '処方箋に印字のQRから読込みました。\n\nOCR処理は行われませんでした。'
              )}
              <Stack bgcolor={'primary.main'} p={2} spacing={1}>
                {medicationAlertOpenButton(() => setMedicationAlertOpen(true))}
                <CustomFab
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.primary.translucent,
                      color: '#fff',
                    },
                    bottom: 90,
                  }}
                  onClick={openModal}
                >
                  <CampaignRoundedIcon style={{ fontSize: 35 }} />
                </CustomFab>
              </Stack>
            </>
          ) : (
            <>
              {selectedPresc.was_error &&
                MessageOverlay(OCR_ERROR_MESSAGE_LEGACY)}
              {!selectedPresc.was_error && MedicationModal}
              <Stack bgcolor={'primary.main'} p={2} spacing={1}>
                {medicationAlertOpenButton(() => setMedicationAlertOpen(true))}
                <CustomFab
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.primary.translucent,
                      color: '#fff',
                    },
                    bottom: 90,
                  }}
                  onClick={openModal}
                >
                  <CampaignRoundedIcon style={{ fontSize: 35 }} />
                </CustomFab>
                {/* TMN系では統合基本結果ウィンドウを使用 */}
                <MergedBasicResultWindow
                  editBasicResult={editBasicResult}
                  reportBasicResult={reportBasicResult}
                  selectedPresc={selectedPresc}
                  isCheckboxDisabled={updatingPrescription}
                  openQrImageModal={openQrImageModal}
                  getHasHighCheckPriority={getHasHighCheckPriority}
                />
              </Stack>
              {renderSnackBar()}
              <Stack px={2} pb={20}>
                {/* TMN系ではRP単位薬品結果表示を使用 */}
                <RPBasedMedicineResult
                  prescription={selectedPresc}
                  reportRPIndex={reportRPIndex}
                />
              </Stack>

              <Box p={2}>
                <RequestIdDisplay
                  requestId={selectedPresc.ocr_request_event_id}
                />
                <Copyright />
              </Box>
            </>
          )}
        </div>
        {AdditionalPrescSnackBar()}
        {renderQrImageModal()}
        {OpenButton}
        {renderCameraModal()}
      </Box>
    </div>
  )
}

export default TMNIndexPage
